import React, { useState } from 'react';
import './MonthlyLandingCard.css';

/**
 * Renders a `monthly_landing.v1` artifact.
 *
 * Pure presentation. Every number here was computed deterministically on the
 * server and persisted on the run — this component rounds for display and
 * nothing else. It shows no approve/apply controls, because an analysis has
 * nothing to approve.
 */

const CONFIDENCE_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };

function money(value, { precise = false } = {}) {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (!precise && abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (!precise && abs >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function signedMoney(value) {
  if (!Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : '−'}${money(Math.abs(value))}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso).slice(0, 10);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}`;
}

function Comparison({ label, comparison }) {
  if (!comparison || !Number.isFinite(comparison.variance)) return null;
  const above = comparison.variance >= 0;
  return (
    <div className={`ml-comparison ${above ? 'ml-comparison--up' : 'ml-comparison--down'}`}>
      <strong>{signedMoney(comparison.variance)}</strong>
      <span> {above ? 'above' : 'below'} {label}</span>
      {Number.isFinite(comparison.variancePct) && (
        <span className="ml-comparison-pct"> ({(comparison.variancePct * 100).toFixed(1)}%)</span>
      )}
    </div>
  );
}

const MonthlyLandingCard = ({ artifact }) => {
  const [showMethod, setShowMethod] = useState(false);
  const data = artifact?.data || {};
  const {
    period = {}, landing = {}, components = [], comparisons = {},
    swingFactors = [], sourceStatus = [], limitations = [],
    backtest = null, revenueTypeDiagnostics = null,
  } = data;

  if (!Number.isFinite(landing.pointEstimate)) {
    return <div className="ml-card ml-card--empty">This run could not produce a landing estimate.</div>;
  }

  return (
    <div className="ml-card">
      <div className="ml-headline">
        <div className="ml-point" title={money(landing.pointEstimate, { precise: true })}>
          {money(landing.pointEstimate)}
        </div>
        <div className="ml-range">
          80% range: {money(landing.low80)}–{money(landing.high80)}
        </div>
      </div>

      <div className="ml-comparisons">
        <Comparison label={comparisons.activeForecast?.label || 'the active forecast'} comparison={comparisons.activeForecast} />
        <Comparison label={comparisons.budget?.label || 'budget'} comparison={comparisons.budget} />
        <Comparison label={comparisons.selectedBudget?.label || 'selected Lodgeic budget version'} comparison={comparisons.selectedBudget} />
      </div>

      <div className="ml-split">
        <div>
          <span className="ml-split-label">Actual to date</span>
          <span className="ml-split-value">{money(landing.actualMtd)}</span>
        </div>
        <div>
          <span className="ml-split-label">Expected remainder</span>
          <span className="ml-split-value">{money(landing.expectedRemainder)}</span>
        </div>
      </div>

      {components.length > 0 && (
        <table className="ml-components">
          <tbody>
            {components.map(component => (
              <tr key={component.key}>
                <td className="ml-component-label">{component.label}</td>
                <td className="ml-component-value" title={money(component.pointEstimate, { precise: true })}>
                  {money(component.pointEstimate)}
                </td>
                <td className="ml-component-range">
                  {Number.isFinite(component.low80) ? `${money(component.low80)}–${money(component.high80)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="ml-cutoffs">
        <span className={`ml-confidence ml-confidence--${landing.confidence}`}>
          {CONFIDENCE_LABEL[landing.confidence] || landing.confidence}
        </span>
        <span>Actuals through {shortDate(period.actualThrough)}</span>
        {sourceStatus.map(source => (
          <span key={source.source}>
            {source.available === false
              ? `${source.label} unavailable`
              : `${source.label} ${source.asOf ? shortDate(source.asOf) : ''}`}
          </span>
        ))}
      </div>

      {swingFactors.length > 0 && (
        <div className="ml-swings">
          <div className="ml-section-title">What could move it</div>
          {swingFactors.map((factor, i) => (
            <div key={i} className={`ml-swing ml-swing--${factor.direction || 'uncertainty'}`}>
              <span className="ml-swing-label">{factor.label}</span>
              {Number.isFinite(factor.sensitivity) && (
                <span className="ml-swing-amount">±{money(Math.abs(factor.sensitivity))}</span>
              )}
              {factor.evidenceIds?.length > 0 && (
                <span className="ml-swing-evidence">{factor.evidenceIds.join(', ')}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {limitations.length > 0 && (
        <ul className="ml-limitations">
          {limitations.map((limitation, i) => <li key={i}>{limitation}</li>)}
        </ul>
      )}

      <button className="ml-method-toggle" onClick={() => setShowMethod(v => !v)}>
        {showMethod ? 'Hide' : 'Show'} method &amp; backtest
      </button>
      {showMethod && (
        <div className="ml-method">
          {components.map(component => (
            <div key={component.key} className="ml-method-row">
              <strong>{component.label}</strong>: {component.method}
              {Number.isFinite(component.backtestWape) && <> · WAPE {(component.backtestWape * 100).toFixed(1)}%</>}
              {Number.isFinite(component.bias) && <> · bias {(component.bias * 100).toFixed(1)}%</>}
              {Number.isFinite(component.sampleSize) && <> · {component.sampleSize} cutoffs</>}
              {component.evidenceIds?.length > 0 && <> · estimate {component.evidenceIds.join(', ')}</>}
              {component.backtestEvidenceIds?.length > 0 && <> · range {component.backtestEvidenceIds.join(', ')}</>}
            </div>
          ))}
          {backtest && (
            <div className="ml-method-row">
              Replay: {backtest.cutoffsUsable}/{backtest.cutoffsAttempted} historical cutoff(s) usable
              {backtest.trailingHistoryAvailable === false && <> · no trailing training history</>}
              {backtest.skipped?.length > 0 && (
                <> · skipped {backtest.skipped.map(s => `${s.cutoffId} (${s.reason})`).join('; ')}</>
              )}
            </div>
          )}
          {revenueTypeDiagnostics?.unmappedTypes?.length > 0 && (
            <div className="ml-method-row">
              Unclassified revenue types: {revenueTypeDiagnostics.unmappedTypes.join(', ')}
            </div>
          )}
          {period.timezone && <div className="ml-method-row">Period boundaries in {period.timezone}</div>}
        </div>
      )}
    </div>
  );
};

export default MonthlyLandingCard;
